/**
 * PortRelayModule — the port run's RELAY card in flight (space campaign E, 2026-09-30).
 *
 * The relay is the rescue the report asks for (§7.4, §17.2, §18.5): ONE authorised custodian substitution of the
 * SAME 24-unit manifest. In the sector a pilot authorised to take a run over (the captain's pre-authorised partner,
 * or the pilot the captain offered it to) sees a card naming the run, the custodian, the distance between the two
 * hulls and the rule (within 300 m, hold 4 s matching speed); "Take over" begins the server's hold and completes it.
 * The captain carrying a run with a live offer sees who may take it. No key of its own (THE ONE-KEY LAW: the sector's
 * keys are B's table) — the card is clicked (Esc frees the mouse) or the dock's Haulage tab is used when both hulls
 * are docked at one station. Reads only the port rows + player_state; every act is the window's reducer call.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { portWindow } from '../lib/port_window.js';
import { ensurePortRows, runs, termsOf, relayRole, nameOf, me, isZero, type HaulRow } from '../lib/port_client.js';
import { RELAY_RANGE_M, RELAY_HOLD_S } from '@shared/space_economy.js';
import type { Identity } from 'spacetimedb';

export class PortRelayModule implements IModule {
  readonly name = 'PortRelay';
  private card: HTMLDivElement | null = null;
  private text: HTMLDivElement | null = null;
  private btn: HTMLButtonElement | null = null;
  private timer = 0;
  private haulId: bigint | null = null;
  private busy = false;

  init(_app: App): void {
    const card = document.createElement('div');
    card.dataset.portRelayCard = '1';
    card.style.cssText = 'position:fixed;top:96px;right:16px;z-index:35;display:none;max-width:min(360px, calc(100vw - 32px));box-sizing:border-box;'
      + 'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-accent);border-radius:var(--ax-win-radius);padding:10px 12px;'
      + 'font:12px var(--ax-font);color:var(--ax-text);box-shadow:0 4px 14px rgba(0,0,0,.45);pointer-events:auto';
    const head = document.createElement('div');
    head.textContent = '⇄ PORT RUN — RELAY';
    head.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-hi);letter-spacing:1px;margin-bottom:4px';
    const text = document.createElement('div');
    text.style.cssText = 'line-height:1.4';
    const btn = document.createElement('button');
    btn.textContent = '⇄ Take over the run';
    btn.dataset.portAct = 'relay-card';
    btn.style.cssText = 'margin-top:8px;background:var(--ax-accent);border:1px solid var(--ax-accent);border-radius:var(--ax-radius);color:var(--ax-on-accent);padding:5px 12px;cursor:pointer;font:700 12px var(--ax-mono);min-height:30px';
    btn.onclick = () => { void this.take(); };
    const hint = document.createElement('div');
    hint.textContent = 'Esc frees the mouse · the dock\'s Haulage tab works when you are both docked';
    hint.style.cssText = 'margin-top:6px;font:10px var(--ax-mono);color:var(--ax-muted)';
    card.append(head, text, btn, hint);
    document.body.appendChild(card);
    this.card = card; this.text = text; this.btn = btn;
    this.timer = window.setInterval(() => this.tick(), 500);
    (window as unknown as { __portRelay?: unknown }).__portRelay = { state: () => this.state(), take: () => this.take() };
  }

  private pos(id: Identity): { x: number; y: number; z: number } | null {
    try {
      const p = atlas.conn?.db.playerState.identity.find(id);
      return p ? { x: p.posX, y: p.posY, z: p.posZ } : null;
    } catch { return null; }
  }

  private tick(): void {
    if (!this.card || !this.text || !this.btn) return;
    if (!atlas.conn || !atlas.spaceFlight) { this.card.style.display = 'none'; this.haulId = null; return; }
    ensurePortRows();
    const mine = me();
    let show: { h: HaulRow; role: 'receiver' | 'captain' } | null = null;
    for (const h of runs()) {
      const role = relayRole(h);
      if (role === 'receiver') { show = { h, role }; break; }
      if (role === 'captain') {
        const t = termsOf(h.haulId);
        if (t && (!isZero(t.offerTo) && Number(t.offerExpiresAt.microsSinceUnixEpoch / 1000n) > Date.now() || !isZero(t.relayPartner))) show = { h, role };
      }
    }
    if (!show || !mine) { this.card.style.display = 'none'; this.haulId = null; return; }
    this.haulId = show.h.haulId;
    const t = termsOf(show.h.haulId);
    const a = this.pos(mine), b = this.pos(show.h.hauler);
    const dist = a && b ? Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) : null;
    const dTxt = dist === null ? 'distance unknown' : dist < 10_000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`;
    if (show.role === 'receiver') {
      this.text.textContent = `${nameOf(t?.captain)}'s run — ${show.h.qty} u ore, the port's — is yours to take over ONCE. `
        + `Custodian ${nameOf(show.h.hauler)} is ${dTxt} away; close to ${RELAY_RANGE_M} m and hold ${RELAY_HOLD_S} s matching speed.`;
      this.btn.style.display = '';
      this.btn.disabled = this.busy;
      this.btn.textContent = this.busy ? '⏳ Holding beside the hull…' : (dist !== null && dist <= RELAY_RANGE_M ? '⇄ Take over the run' : `⇄ Take over (close to ${RELAY_RANGE_M} m first)`);
    } else {
      const who = t && !isZero(t.offerTo) ? nameOf(t.offerTo) : nameOf(t?.relayPartner);
      this.text.textContent = `Your port run (${show.h.qty} u) may be taken over ONCE by ${who} — they close to ${RELAY_RANGE_M} m of your hull and hold ${RELAY_HOLD_S} s. The deadline and the reward do not change.`;
      this.btn.style.display = 'none';
    }
    this.card.style.display = 'block';
  }

  private async take(): Promise<boolean> {
    if (this.haulId === null || this.busy) return false;
    this.busy = true;
    this.tick();
    try { return await portWindow().takeOver(this.haulId); }
    finally { this.busy = false; this.tick(); }
  }

  private state(): Record<string, unknown> {
    return { shown: this.card?.style.display === 'block', haulId: this.haulId === null ? null : String(this.haulId), text: this.text?.textContent ?? '', busy: this.busy };
  }

  dispose(): void { window.clearInterval(this.timer); this.card?.remove(); }
}
